import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { colors, fontStyles, spacing, buildPageSpacing } from '../../styles';
import { investmentIndex } from '../../apis/search';
import * as storage from '../../storage';
import * as stores from '../../stores';
import Page from '../Page';
import Header from '../Header';
import magnifyingGlassUrl from '../Search/magnifyingGlass.svg';
import crossIcon from '../Search/cross.svg';
import OnboardingStockLogo from '../OnboardingView/Components/OnboardingStockLogo';
import { safeGetItem, safeSetItem, safeRemoveItem } from '../../utils/safeStorage';
import { PORTFOLIO_STORAGE_KEY } from './portfolioSelection';

interface RouteParams {
  id?: string;
}

interface SelectedTicker {
  symbol: string;
  name: string;
}

const AddTickers: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<RouteParams>();
  const history = useHistory();
  const [query, setQuery] = useState('');

  // Check if this is creating a new portfolio
  const isNewPortfolio = id === 'new' || id === 'undefined' || !id;

  // Get existing portfolio data from store
  const { portfolios } = stores.Portfolios.useState();
  const existingPortfolio = !isNewPortfolio ? portfolios.find((p) => p.id === id) : null;

  const [selectedTickers, setSelectedTickers] = useState<SelectedTicker[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Initialize selected tickers from existing portfolio or localStorage
  useEffect(() => {
    if (isInitialized) return;

    if (isNewPortfolio) {
      // For new portfolios, load from localStorage
      const saved = safeGetItem('addTickers_new');
      if (saved) {
        try {
          setSelectedTickers(JSON.parse(saved));
        } catch {
          // Corrupted localStorage data
        }
      }
      setIsInitialized(true);
    } else if (existingPortfolio) {
      // For existing portfolios, check localStorage first (for unsaved changes),
      // otherwise don't pre-populate - this is for ADDING new symbols
      const storageKey = `addTickers_${id}`;
      const saved = safeGetItem(storageKey);
      if (saved) {
        try {
          setSelectedTickers(JSON.parse(saved));
        } catch {
          // Corrupted localStorage data
        }
      }
      // Don't pre-populate with existing symbols - this page is for adding NEW symbols
      setIsInitialized(true);
    }
  }, [isNewPortfolio, existingPortfolio, id, isInitialized]);

  // Save selected tickers to localStorage whenever they change
  useEffect(() => {
    const storageKey = isNewPortfolio ? 'addTickers_new' : `addTickers_${id}`;
    safeSetItem(storageKey, JSON.stringify(selectedTickers));
  }, [selectedTickers, id, isNewPortfolio]);

  const resultsQuery = useQuery(
    ['search', query],
    async () => {
      if (!query) return [];
      const excludedSymbols = selectedTickers.map((t) => t.symbol);
      const facetFilters = excludedSymbols.map((symbol) => `objectID:-${symbol}`);

      const results = (
        await investmentIndex.search(query, {
          facetFilters,
          hitsPerPage: 5,
        })
      ).hits;
      return results.filter((result: any) => !!result.name);
    },
    { enabled: !!query },
  );

  const isResultsShown = query && !resultsQuery.isLoading;

  const handleClickCross = () => {
    setQuery('');
  };

  const handleAddTicker = (symbol: string, name: string) => {
    // Add to selected list if not already present
    if (!selectedTickers.find((t) => t.symbol === symbol)) {
      setSelectedTickers((prev) => [...prev, { symbol, name }]);
    }
    setQuery('');
  };

  const handleRemoveTicker = (symbol: string) => {
    setSelectedTickers((prev) => prev.filter((t) => t.symbol !== symbol));
  };

  const handleContinue = async () => {
    if (selectedTickers.length === 0) return;

    setIsSaving(true);
    try {
      if (isNewPortfolio) {
        // Create new portfolio
        const portfolioName = safeGetItem('newPortfolioName', 'session') || 'New Portfolio';
        console.log('Creating portfolio with name:', portfolioName, 'and tickers:', selectedTickers);
        const portfolioId = await storage.savePortfolio({
          title: portfolioName,
          strategy: '',
          symbols: selectedTickers.map((t) => t.symbol),
          allocations: [],
        });

        console.log('Portfolio created with ID:', portfolioId);

        // Clear temporary storage
        safeRemoveItem('newPortfolioName', 'session');
        safeRemoveItem('addTickers_new');

        // Select this portfolio and redirect to main portfolios page
        safeSetItem(PORTFOLIO_STORAGE_KEY, portfolioId);
        history.push('/portfolios');
      } else {
        // Update existing portfolio - merge new symbols with existing ones
        const portfolios = await storage.getPortfolios();
        const portfolio = portfolios.find((p) => p.id === id);

        if (portfolio) {
          // Merge existing symbols with new selected tickers (avoid duplicates)
          const existingSymbols = portfolio.symbols || [];
          const newSymbols = selectedTickers.map((t) => t.symbol);
          const mergedSymbols = [...new Set([...existingSymbols, ...newSymbols])];

          const updatedPortfolio = {
            ...portfolio,
            symbols: mergedSymbols,
          };

          await storage.updatePortfolio(updatedPortfolio);

          // Clear the saved state since we successfully saved
          safeRemoveItem(`addTickers_${id}`);

          // Select this portfolio and redirect to main portfolios page
          safeSetItem(PORTFOLIO_STORAGE_KEY, id);
          history.push('/portfolios');
        }
      }
    } catch (error) {
      console.error('Error saving tickers:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const searchContainerStyles = css`
    position: relative;
    margin-bottom: ${spacing.regular};
  `;

  const inputStyles = css`
    ${fontStyles.cat};
    width: 100%;
    height: 50px;
    padding: 14px 16px 14px 50px;
    border: 2px solid ${colors.cloud};
    border-radius: 8px;
    outline: none;
    background-color: ${colors.sugar};
    color: ${colors.liquorice};

    html[data-theme='dark'] & {
      background-color: #242424;
      color: ${colors.sugar};
      border-color: ${colors.tungsten};
    }

    &:focus {
      border-color: ${colors.petrolBlue};
    }

    &::placeholder {
      color: ${colors.silver};
    }

    ${isResultsShown &&
    css`
      border-radius: 8px 8px 0 0;
    `}
  `;

  const selectedTickersStyles = css`
    display: flex;
    flex-wrap: wrap;
    gap: ${spacing.milli};
    margin-bottom: ${spacing.regular};
    min-height: 40px;
  `;

  const tickerChipStyles = css`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    background: ${colors.petrolBlue}10;
    border-radius: 16px;
    ${fontStyles.fairyfly};
  `;

  const tickerSymbolStyles = css`
    color: ${colors.petrolBlue};
    font-weight: 500;
    line-height: 1;
  `;

  const removeButtonStyles = css`
    background: none;
    border: none;
    color: ${colors.petrolBlue};
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      color: ${colors.berry};
    }
  `;

  const continueButtonStyles = css`
    display: block;
    width: 100%;
    background-color: ${colors.petrolBlue};
    color: ${colors.snow};
    border: none;
    border-radius: 8px;
    padding: ${spacing.deci};
    ${fontStyles.horse};
    cursor: pointer;
    opacity: ${selectedTickers.length === 0 ? 0.5 : 1};
    font-weight: 500;
    transition: all 0.2s ease;

    &:hover {
      opacity: ${selectedTickers.length === 0 ? 0.5 : 0.9};
    }
  `;

  const resultsContainerStyles = css`
    position: absolute;
    z-index: 1;
    width: 100%;
    background-color: ${colors.sugar};
    border: 1px solid ${colors.cloud};
    border-top: none;
    border-radius: 0 0 8px 8px;

    html[data-theme='dark'] & {
      background-color: #242424;
      border-color: ${colors.tungsten};
    }
  `;

  const resultItemStyles = css`
    display: flex;
    align-items: center;
    padding: ${spacing.deci};
    cursor: pointer;
    border-bottom: 1px solid ${colors.cloud};

    &:hover {
      background-color: ${colors.fog};
    }

    &:last-child {
      border-bottom: none;
    }

    html[data-theme='dark'] & {
      border-color: ${colors.tungsten};

      &:hover {
        background-color: ${colors.tungsten};
      }
    }
  `;

  return (
    <Page>
      <Header
        headingText=""
        onBack={() => {
          if (isNewPortfolio) {
            history.push('/portfolios/add');
          } else {
            // Go back to main portfolios page with this portfolio selected
            safeSetItem(PORTFOLIO_STORAGE_KEY, id!);
            history.push('/portfolios');
          }
        }}
      />
      <div
        css={css`
          padding: ${spacing.milli};
          padding-bottom: calc(80px + ${spacing.regular}); /* Space for fixed button */
        `}
      >
        <h1
          css={css`
            ${fontStyles.shark};
            margin: 0 0 ${spacing.deci};
          `}
        >
          {t('portfolios.addTickers.title')}
        </h1>
        <p
          css={css`
            ${fontStyles.dog};
            color: ${colors.silver};
            margin-bottom: ${spacing.regular};
          `}
        >
          {t('portfolios.addTickers.instruction')}
        </p>

        <div css={searchContainerStyles}>
          <span
            css={css`
              position: absolute;
              height: 50px;
              width: 50px;
              background-color: transparent;
              border: none;
              outline: none;
              padding: 14px;
              pointer-events: none;
              z-index: 2;
            `}
          >
            <img src={magnifyingGlassUrl} alt="" />
          </span>

          <input
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('portfolios.addTickers.searchPlaceholder')}
            css={inputStyles}
          />

          {query.length > 0 && (
            <button
              type="button"
              onClick={handleClickCross}
              css={css`
                position: absolute;
                height: 50px;
                width: 50px;
                right: 0;
                top: 0;
                background-color: transparent;
                border: none;
                outline: none;
                cursor: pointer;
                padding: 18px;
                z-index: 2;
              `}
              aria-label={t('portfolios.addTickers.cancel')}
            >
              <img src={crossIcon} alt="" />
            </button>
          )}

          {isResultsShown && (
            <div css={resultsContainerStyles}>
              {resultsQuery.data?.map((result: any) => (
                <div
                  key={result.symbol}
                  css={resultItemStyles}
                  onClick={() => handleAddTicker(result.symbol, result.name)}
                >
                  <OnboardingStockLogo symbol={result.symbol} />
                  <div
                    css={css`
                      margin-left: ${spacing.deci};
                    `}
                  >
                    <div
                      css={css`
                        ${fontStyles.dog};
                        font-weight: 600;
                      `}
                    >
                      {result.symbol}
                    </div>
                    <div
                      css={css`
                        ${fontStyles.ant};
                        color: ${colors.silver};
                      `}
                    >
                      {result.name}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div css={selectedTickersStyles}>
          {selectedTickers.map((ticker) => (
            <div key={ticker.symbol} css={tickerChipStyles}>
              <OnboardingStockLogo symbol={ticker.symbol} size="small" />
              <span css={tickerSymbolStyles}>{ticker.symbol}</span>
              <button
                css={removeButtonStyles}
                onClick={() => handleRemoveTicker(ticker.symbol)}
                aria-label={t('portfolios.addTickers.removeSymbol', { symbol: ticker.symbol })}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Fixed continue button at bottom */}
      <div
        css={css`
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          padding: ${spacing.regular};
          padding-bottom: ${buildPageSpacing(spacing.regular, 'BOTTOM')};
          background-color: ${colors.sugar};
          border-top: 1px solid ${colors.cloud};
          z-index: 1000;
          transition: bottom 0.3s ease;

          html[data-theme='dark'] & {
            background-color: #1a1a1a;
            border-color: ${colors.tungsten};
          }
        `}
      >
        <button css={continueButtonStyles} onClick={handleContinue} disabled={selectedTickers.length === 0 || isSaving}>
          {isSaving
            ? isNewPortfolio
              ? t('portfolios.addTickers.creating')
              : t('portfolios.addTickers.saving')
            : isNewPortfolio
              ? t('portfolios.addTickers.createPortfolio')
              : t('portfolios.addTickers.continue')}
        </button>
      </div>
    </Page>
  );
};

export default AddTickers;
